import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { emptyLessonObj } from '@app/account/profile/user-profile.variables';
import { DictionaryService } from '@app/core/services/bff/dictionary.service';
import { findSubjectById, HelpersBffService } from '@app/core/services/bff/helpers.service';
import { UserProfileBffService } from '@app/core/services/bff/user-profile.service';
import { LoadingBarService } from '@core/components/spinners/loading-bar/loading-bar.service';
import {
  AccountService,
  AuthService,
  ContentService,
  ContextService,
  FeaturesService,
  LanguageService,
  LessonsBlockType,
  MetaService,
  ProfileService,
} from '@core/services';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { BffGetUserLessonsEndpoint } from 'lingo2-api-models';
import {
  EContentOrder,
  EContentPanelRoute,
  FindContentStrategyEnum,
  IFindContentFilter,
  ITeachingSubject,
  Lesson,
  SubjectCategoryEnum,
  UserService,
  UserServiceCategory,
  UserStatusEnum,
} from 'lingo2-models';
import { uniqBy } from 'lodash';
import { DeviceDetectorService } from 'ngx-device-detector';
import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, takeUntil, tap } from 'rxjs/operators';
import { BaseUserProfileSubpageComponent } from '../base-user-profile-subpage.component';
import { GridCardService } from '@app/library/panes/grid-card-universal/grid-card.service';
import { ApplicationDialogsService } from '@app/shared/application-dialogs';

interface ITeachingSubjectItem {
  value: string | number;
  title: string;
  subject: ITeachingSubject;
  total?: number;
}

@Component({
  selector: 'app-user-profile-lessons-subpage',
  templateUrl: './user-profile-lessons-subpage.component.html',
  styleUrls: ['./user-profile-lessons-subpage.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileLessonsSubpageComponent extends BaseUserProfileSubpageComponent implements OnInit {
  @Input() view: string;

  @Input()
  public set selectedSubjectId(selectedSubjectId: string) {
    if (this._selectedSubjectId !== selectedSubjectId) {
      this._selectedSubjectId = selectedSubjectId;
      this.subjectId = selectedSubjectId;
      this.initTeachingSubject();
    }
  }

  public get selectedSubjectId(): string {
    return this._selectedSubjectId;
  }

  @Output() onChangeSubject = new EventEmitter<string>();
  @Output() loaded = new EventEmitter<boolean>();
  public teachingSubjectsList: ITeachingSubjectItem[] = [];
  public filteredTeachingSubjectsList: ITeachingSubjectItem[] = [];
  public teachingSubjectIndex = -1;
  public teachingSubjectMore = false;
  public isContent = true;

  public emptyLessonObj = emptyLessonObj;

  public activeServiceIndex = -1;
  public checkoutService: UserService;
  public isMediaPrepared = false;
  public teachingSubject: ITeachingSubject;
  public selectUserServicesFilter: UserServiceCategory;
  public openedUserService: UserService;
  public routes = EContentPanelRoute;

  public lessons: LessonsBlockType = {
    items: [],
    loading: false,
    loaded: false,
    filter: {},
    pagination: {
      page: 1,
      pageSize: 10,
    },
  };

  private subjectId: string;
  private _selectedSubjectId: string;

  constructor(
    public errorNotificationService: ErrorNotificationService,
    protected router: Router,
    protected authService: AuthService,
    protected contextService: ContextService,
    protected contentService: ContentService,
    protected profileService: ProfileService,
    protected userProfileBff: UserProfileBffService,
    protected features: FeaturesService,
    protected gridCardService: GridCardService,
    protected dialogsService: ApplicationDialogsService,
    protected helperService: HelpersBffService,
    protected dictionaryService: DictionaryService,
    protected languageService: LanguageService,
    protected meta: MetaService,
    public deviceService: DeviceDetectorService,
    protected loadingBar: LoadingBarService,
    protected cdr: ChangeDetectorRef,
  ) {
    super(
      router,
      authService,
      contextService,
      profileService,
      dictionaryService,
      languageService,
      userProfileBff,
      features,
      meta,
      loadingBar,
      cdr,
    );
  }

  private get getSubjectId$(): Observable<string> {
    return this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event: NavigationEnd) => {
        this.subjectId = this.selectedSubjectId ? this.selectedSubjectId : this.parseSubjectIdFromUrl(event.url);
        return this.subjectId;
      }),
      distinctUntilChanged(),
    );
  }

  private init(): void {
    this.loading = true;
    this.subjectId = this.selectedSubjectId ? this.selectedSubjectId : this.router.url.split('/').pop();
    this.prepareCardByProfile();
    this.markForCheck();

    this.getSubjectId$.pipe(distinctUntilChanged(), takeUntil(this.destroyed$)).subscribe({
      next: () => this.initTeachingSubject(),
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });

    this.initTeachingSubject();
  }

  public ngOnInit(): void {
    this.init();
  }

  protected prepareCardByProfile() {
    if (!this._user || !this._profile || !this.subjects.length) {
      return;
    }

    this.accountRoute = AccountService.accountRoute(this._user).join('/');
    this.teachingSubjectsList = (this._profile.teachingSubjects || [])
      .filter((s) => s.subjectId > 0)
      .map((subject, index) => {
        const _subject = this.subjects.find((s) => s.id === +subject.subjectId);
        return {
          value: index,
          title: (_subject ? _subject.title : null) || '---',
          route: AccountService.accountRoute(this._user, ['teaching', subject.subjectId.toString()]),
          native: findSubjectById(this.subjects, subject)?.category_id === SubjectCategoryEnum.native_language,
          subject: {
            subject_id: subject.subjectId,
            subject: null,
            subject_other_name: null,
            subject_other_id: null,
            description: subject.description,
            video_url: subject.videoUrl,
            video_id: subject.videoId,
            video: null,
            video_mode: subject.videoMode,
          },
        };
      });

    this.teachingSubjectsList = uniqBy(this.teachingSubjectsList, (e) => e.subject.subject_id);

    const _filter: Partial<IFindContentFilter> = {
      use: FindContentStrategyEnum.profile,
      order: EContentOrder.date_desc,
      author_id: this._user.id,
    };

    this.teachingSubjectsList.forEach((el) => {
      _filter.subject_id = el.subject.subject_id;
    });

    this.isMe = this._me && this._user && this._me?.id === this._user.id;
    this.isGuest = this._me && this._me?.status === UserStatusEnum.guest;
  }

  protected initTeachingSubject() {
    if (this.subjectId && this.teachingSubjectsList.length) {
      const item = this.teachingSubjectsList.find((s) => s.subject.subject_id === +this.subjectId);
      this.teachingSubject = item ? item.subject : null;

      this.loadLessons();
    } else {
      this.isContent = false;
    }
  }

  protected loadLessons() {
    if (!this._user || !this.teachingSubject) {
      return;
    }

    const _filter: BffGetUserLessonsEndpoint.Query = {
      page: this.lessons.pagination.page,
      authorId: this._user.id,
      pageSize: 10,
    };

    _filter.subjectId = [Number(this.teachingSubject.subject_id)];

    this.userProfileBff
      .getUserLesson(_filter)
      .pipe(
        catchError((error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.lessons.loading = false;
          this.loaded.emit(true);
          this.detectChanges();
          return EMPTY;
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (response) => {
          const legacyLessons: Lesson[] = this.helperService.convertBffLessonsToLegacy(response.data).map((lesson) => {
            lesson.author = this._user;
            return lesson;
          });

          this.lessons.items = [...this.lessons.items, ...legacyLessons];

          this.lessons.pagination = response.pagination;

          this.lessons.loading = false;
          this.lessons.loaded = true;
          this.loaded.emit(true);
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.lessons.loading = false;
          this.loaded.emit(true);
          this.detectChanges();
        },
      });
  }

  public setSubjectId(subjectId: string) {
    this._selectedSubjectId = subjectId;
    this.onChangeSubject.emit(subjectId);
    this.init();
  }

  private parseSubjectIdFromUrl(url: string): string {
    return this.router.url.split('/').pop();
  }

  public onInViewportBottomChange(reached) {
    if (reached.visible && !this.lessons.loading) {
      if (this.lessons.pagination.page < this.lessons.pagination.totalPages) {
        this.loadNextPage();
      }
    }
  }

  public loadNextPage() {
    if (this.lessons.loading) {
      return;
    }
    this.lessons.pagination.page += 1;
    this.loadLessons();
  }

  public onOpenCard(event) {
    this.gridCardService.setModalData(event);
    this.dialogsService.openGridCardModal(this.gridCardService.modalData);
    this.detectChanges();
  }
}
